import { useCallback, useContext, useMemo } from "react";
import { ImportIds, ImportService } from "../../../services/ImportService";
import RequestService from "../../../services/RequestService";
import { formatDateShort, formatDateTimeDigits, formatTimeLong } from "../../../utils/FormatUtils";
import ModalService from "../../shared/bootstrap/Modal";
import ToastContext from "../../shared/bootstrap/Toast";
import { ImportLogListResponse } from "../../shared/entities/ImportLogListResponse";
import FloatingPanelService from "../../shared/FloatingPanel";
import { TranslationService } from "../../../services/TranslationService";
import TableContext from "../../task/TableContext";
import { ImportTableContextValues } from "./ImportLog";
import ImportLogDetail from "./ImportLogDetail";
import CompanyService from "../../../services/CompanyService";
import TooltipComponent from "../../shared/TooltipComponent";
import { useHistory } from "react-router-dom";

const ImportLogItem = ({ data }: { data: ImportLogListResponse.Item }) => {
    const { translate } = TranslationService;
    const { showToast } = useContext(ToastContext);
    const { reload, isFirstImport } = useContext(TableContext) as ImportTableContextValues;
    const status = (!data.Status && data.Status?.length === 0) ? undefined : TranslationService.getTranslation(data.Status!);
    const importType = ImportService.findImportType(data.Type, data.UpdateStrategy);
    const timezone = CompanyService.getTimezone();
    const lastStatusDate = data.LastStatus && new Date(new Date(data.LastStatus).getTime() - timezone * 60 * 1000);
    const statusTitle = status && status + (lastStatusDate ? (" " + formatDateShort(lastStatusDate) + " " + formatTimeLong(lastStatusDate)) : "");
    const history = useHistory();

    const getResult = (data: ImportLogListResponse.Item) => {
        let resultData = data.IOCount > 0 ? `<a href="/${TranslationService.currentLanguage}/report/iolist?impid=${data.ImportID}">` + (data.IOCount.toString() + " " + translate.Invoice + (data.IOCount !== 1 ? "s" : "")) + "</a>" : "";
        resultData += (data.PersonCount > 0 ? `<a href="/${TranslationService.currentLanguage}/client?impid=${data.ImportID}">` + (((resultData) ? ", " : "") + data.PersonCount.toString() + " " + translate.Client + (data.PersonCount !== 1 ? "s" : "")) + "</a>" : "");
        resultData += (data.TransactionCount > 0 ? `<a href="/${TranslationService.currentLanguage}/report/payment?impid=${data.ImportID}">` + (((resultData) ? ", " : "") + data.TransactionCount.toString() + " " + translate.TransactionSingular + (data.TransactionCount !== 1 ? "s" : "")) + "</a>" : "");
        // eslint-disable-next-line 
        resultData += (data.MessageCount > 0 ? `<a href="/${TranslationService.currentLanguage}/report/reportactivity?impid=${data.ImportID}">` + (((resultData) ? ", " : "") + data.MessageCount.toString() + " " + translate.Comment + (data.MessageCount !== 1 ? "s" + "</a>" : "")) : "");
        resultData += (data.TagCount > 0 ? ((!(resultData) ? ", " : "") + data.TagCount.toString() + " " + translate.Tag + (data.TagCount !== 1 ? "s" : "")) : "");
        resultData += (data.GroupCount > 0 ? ((!(resultData) ? ", " : "") + data.GroupCount.toString() + " " + translate.group + (data.GroupCount !== 1 ? "s" : "")) : "");
        return resultData;
    };

    const showImportDetail = useCallback(
        () => {
            data.ControlSuspended ?
                history.push(`import/finish?id=${data.ImportID}`)
                :
                FloatingPanelService.showPanel({
                    title: translate.Import,
                    children: <ImportLogDetail data={data} />,
                    width: 900,
                    height: 700,
                });
        },
        [data, history, translate.Import])

    const showImport = () =>
        FloatingPanelService.showPanel({
            title: translate.Import,
            children: <ImportLogDetail data={data} />,
            width: 900,
            height: 700,
        });


    const deleteImport = (id: number) => {
        const onDelete = async () => {
            const result = ImportService.delete(id);
            if (result instanceof Error) {
                showToast(translate.ErrorProcessingRequest, undefined, "danger");
            }
            reload();
        };
        ModalService.showDefaultModal({
            title: translate.DeleteImport,
            message: translate.AreYouSureYouWishToDeleteThisImportRecord,
            acceptButtonLabel: translate.Delete,
            acceptButtonClassName: "btn btn-danger",
            onAcceptClick: onDelete,
        });
    };

    const downloadImport = () => {
        RequestService.downloadFile(`/import/download?id=${data.ImportID}`, data.Filename);
    };

    const autoFtp = (
        data.ImportTemplate !== null
        &&
        (
            data.ImportTemplate?.entity === ImportIds.AutomaticInvoiceUpdate ||
            data.ImportTemplate?.entity === ImportIds.AutomaticCustomerUpdate
        )
    )

    const statusDescription = useMemo(() => {
        if (!(data.Paused ?? false)) {
            if (data.ControlSuspended) {
                return translate.ImportSuspended;
            } else {
                switch ((data.Status ?? "").toLocaleLowerCase()) {
                    case "completed":
                        return translate.Complete;
                    case "person":
                        return translate.Clients;
                    case "group":
                        return translate.Groups;
                    case "io":
                        return translate.Invoice + "s";
                    case "transaction":
                        return translate.Payment + "s";
                    case "activity":
                        return translate.Comments;
                    case "io updates":
                        return translate.IOUpdating;
                    case "io-add":
                        return translate.IOAdd;
                    case "start":
                        return translate.Started;
                    case "processed":
                        return translate.processed;
                    case "fallo":
                        return translate.Failed;
                    case "import":
                        return translate.StartingImport;
                    case "processing data":
                        return translate.Processing;
                    case "fail-dates":
                        return translate["Fail-Dates"];
                    case "fail-repeat":
                        return translate.FailRepeat;
                    case "fail-all":
                        return translate["fail-all"];
                    case "fail-overlapping":
                        return translate.FailOverlapping;
                    case "empty-file":
                        return translate.EmptyFile;
                    case "separator-error":
                        return translate.SeparatorError;
                    case "formulas":
                        return translate.Calulating;
                    case "formulas calculated":
                        return "Fórmulas calculadas";
                    case "tags":
                        return translate.Tags;
                    case "canceled":
                        return "Cancelado";
                    case "resume":
                        return "Pendiente de finalización";
                    case "decrypting":
                        return "Desencriptando archivo";
                    case "unziping":
                        return "Descomprimiendo archivo";
                    case "saving templated":
                        return "Grabando archivo";
                    case "processing group":
                        return "Procesando grupos";
                    case "validating additionals":
                        return "Validando adicionales";
                    case "validating updates":
                        return "Validando actualizaciones";
                    case "invoices":
                        return translate.Invoices;
                    case "transactions":
                        return translate.Payment + "s";
                    default:
                        if ((data.Status ?? "").toLowerCase().includes("template")) {
                            return (data.Status ?? "").toLowerCase().replace("template", translate.Template);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("creating-file")) {
                            return (data.Status ?? "").toLowerCase().replace("creating-file", "Creando archivo");
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("dictionary")) {
                            return (data.Status ?? "").toLowerCase().replace("dictionary", translate.Dictionary);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading data 0%")) {
                            return (data.Status ?? "").toLowerCase().replace("reading data 0%", translate["Reading Data 0%"]);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading data")) {
                            return (data.Status ?? "").toLowerCase().replace("reading data", translate["Reading Data"]);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading file 0%")) {
                            return (data.Status ?? "").toLowerCase().replace("reading file 0%", translate["Reading File 0%"]);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading file")) {
                            return (data.Status ?? "").toLowerCase().replace("reading file", translate["Reading File"]);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading person")) {
                            return (data.Status ?? "").toLowerCase()
                                .replace("reading", translate.ReadingData)
                                .replace("person", "(" + translate.Clients + ")");
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading io")) {
                            return (data.Status ?? "").toLowerCase()
                                .replace("reading", translate.ReadingData)
                                .replace("io", "(" + translate.Invoice + "s") + ")";
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("reading")) {
                            return (data.Status ?? "").toLowerCase().replace("reading", translate.ReadingData);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("person-update")) {
                            return (data.Status ?? "").toLowerCase().replace("person-update", translate.UpdatingCustomers);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("invoices udpate")) {
                            return (data.Status ?? "").toLowerCase().replace("invoices udpate", translate.UpdateInvoices);
                        }
                        else if ((data.Status ?? "").toLowerCase().includes("validating new ios")) {
                            return (data.Status ?? "").toLowerCase().replace("validating new ios", "Validando nuevas facturas");
                        }
                        else {
                            return data.Status ?? "";
                        }
                }
            }
        } else {
            return translate.InPause;
        }
    }, [data.ControlSuspended, data.Paused, data.Status, translate])

    const showDelete = () => {
        const showFirstImport = isFirstImport === data.ImportID;
        let show = ((CompanyService.canDo('importdata') && (showFirstImport || data.total === 0)) || CompanyService.getIsAdmin());

        if (data.ImportID !== isFirstImport &&  data.total > 0) {
            show = false;
        }

        return show;
    }

    return (


        <tr className="show-child-on-hover">
            <td width={100} >{importType && TranslationService.getTranslation(importType.text)}</td>
            <td width={100} >{(data.Started ?? data.Created) ? formatDateTimeDigits(new Date(new Date(data.Started ?? data.Created).getTime() - timezone * 60 * 1000)) : ""}</td>
            <td width={100} >{lastStatusDate ? formatDateTimeDigits(lastStatusDate) : ""}</td>

            <td title={statusTitle} onClick={showImportDetail} className="pointer">
                <TooltipComponent title={status || ''}>
                    {statusDescription}
                </TooltipComponent>
            </td>
            <td>
                <div dangerouslySetInnerHTML={{ __html: getResult(data) }} />
            </td>
            <td>
                <TooltipComponent title={autoFtp ? "AUTO" : data.FTP ? 'FTP' : data.UserName || ''}>
                    {autoFtp ? "AUTO" : data.FTP ? 'FTP' : data.UserName}
                </TooltipComponent>
            </td>
            <td>
                <TooltipComponent title={data.ImportTemplate?.Name || ''}>
                    {data.ImportTemplate?.Name}
                </TooltipComponent>
            </td>
            <td width={100}>
                <TooltipComponent title={data.Filename || ''}>
                    {data.Filename}
                </TooltipComponent>
            </td>
            <td>
                <i className="show-when-hovering-parent fa-regular fa-arrow-down-to-line pointer" onClick={downloadImport}></i>
                <i className={"pointer fa-regular fa-search show-when-hovering-parent mx-3"} onClick={showImport} />
                {
                    showDelete() &&
                    <i className={"pointer fa-regular fa-trash-can text-danger show-when-hovering-parent"} onClick={() => deleteImport(data.ImportID)} />
                }
            </td>
        </tr>);
};
export default ImportLogItem;